import L from 'leaflet'
import '@/helpers/leaflet-canvas-markers'
import { circleMarkerText } from '@/helpers/leaflet-circle-marker-text'
import { polylineDecorated } from '@/helpers/leaflet-polyline-decorated'
import { evaluateExpression } from '@/helpers/evaluate-expression'

const STYLE_ITEM_CLASS_FACTORY_MAP = {
  CircleMarker: L.circleMarker,
  CircleMarkerText: circleMarkerText,
  CanvasMarker: L.canvasMarker,
  GeoJSON: L.geoJSON,
  Polyline: L.polyline,
  PolylineDecorated: polylineDecorated
}

const DEFAULT_STYLE_OPTIONS = {
  CircleMarker: {
    radius: 17,
    stroke: true,
    weight: 3,
    color: '#fff',
    fill: true,
    fillOpacity: 1
  }
}

const CANVAS_WIDTH = 22
const CANVAS_HEIGHT = 22

/**
 * Словарь функций рисования иконок для легенды
 * ключ - тип стиля
 * значение - функция, которая на основе стиля нарисует иконку
 */
const DRAW_LEGEN_ICON_BY_STYLE = {
  CircleMarker: drawCircleMarkerLegendIcon,
  CircleMarkerText: drawCircleMarkerLegendIcon,
  Polyline: drawPolylineLegendIcon,
  PolylineDecorated: drawPolylineDecoratedLegendIcon,
  GeoJSON: drawGeoJsonLegendIcon,
  VectorTile: drawVectorTileIcon
}

/**
 * Ключ - тип стиля
 * значение - массив путей к названиям иконок
 * рядом с полем по этому пути добавится url к иконке в соответствии со словарем иконок
 */
export const PARSE_ICON_LINKS = {
  CanvasMarker: ['img.icon'],
  PolylineDecorated: ['decorator.options.img.icon'],
  VectorTile: ['default.options.decorator.options.img.icon'] //TODO: сделать более универсально
}

export function createLayerItem({ layerItemCoordinates, layerItemProperties, layerItemStyle, styleConfig, paneName, renderer, layerIconDictionary = {} }) {
  //Если настройки стиля - массив
  if (Array.isArray(styleConfig)) {
    //Если массив пустой, то не можем создать объект без стиля
    if (styleConfig.length === 0) {
      return null
    }

    let layerItems = []
    styleConfig.forEach((styleConfigItem) => {
      if (styleConfigItem.hasOwnProperty('style')) {
        //Формат с правилами
        //Немного костыльно, считаем что работаем с правилом, если внутри есть поле style

        //Проверка фильтра правила
        if (styleConfigItem.filter && !evaluateExpression(styleConfigItem.filter, layerItemProperties)) {
          return
        }

        if (Array.isArray(styleConfigItem.style)) {
          //Обработка массива стилей внутри правила
          if (styleConfigItem.style.length === 0) {
            return
          }

          styleConfigItem.style.forEach((styleConfigItemInsideRule) => {
            const layerItem = createLayerItemByStyleConfig({
              layerItemCoordinates,
              layerItemProperties,
              layerItemStyle,
              styleConfig: styleConfigItemInsideRule,
              paneName,
              renderer,
              layerIconDictionary
            })
            if (layerItem !== null) {
              layerItems.push(layerItem)
            }
          })
        } else {
          //Обработка одиничного стиля внутри правила
          const layerItem = createLayerItemByStyleConfig({
            layerItemCoordinates,
            layerItemProperties,
            layerItemStyle,
            styleConfig: styleConfigItem.style,
            paneName,
            renderer,
            layerIconDictionary
          })
          if (layerItem !== null) {
            layerItems.push(layerItem)
          }
        }
      } else {
        //Формат без правил
        const layerItem = createLayerItemByStyleConfig({
          layerItemCoordinates,
          layerItemProperties,
          layerItemStyle,
          styleConfig: styleConfigItem,
          paneName,
          renderer,
          layerIconDictionary
        })

        if (layerItem !== null) {
          layerItems.push(layerItem)
        }
      }
    })

    //Возвращаем составной объект по каждому стилю
    if (layerItems.length > 0) {
      return L.featureGroup(layerItems)
    }

    return null
  }

  //Если настройки стиля содержат единичный объект, то рисуем для объекта слоя отдельный объект
  if (typeof styleConfig === 'object') {
    return createLayerItemByStyleConfig({
      layerItemCoordinates,
      layerItemProperties,
      layerItemStyle,
      styleConfig,
      paneName,
      renderer,
      layerIconDictionary
    })
  }

  //Если не получили настройки стиля
  return null
}

function createLayerItemByStyleConfig({ layerItemCoordinates, layerItemProperties, layerItemStyle, styleConfig, paneName, renderer, layerIconDictionary }) {
  //Проверка фильтрующего выражения для стиля
  if (styleConfig.filter && !evaluateExpression(styleConfig.filter, layerItemProperties)) {
    return null
  }

  return STYLE_ITEM_CLASS_FACTORY_MAP[styleConfig.type](layerItemCoordinates, {
    pane: paneName,
    renderer,
    ...evaluateStyleOptions(styleConfig, layerItemProperties, layerItemStyle, layerIconDictionary),
    properties: {
      ...layerItemProperties
    }
  })
}

export function evaluateStyleOptions(styleConfig, layerItemProperties, layerItemStyle, layerIconDictionary) {
  //Получение стиливых настроек объекта Leaflet для создания
  let options = {
    ...DEFAULT_STYLE_OPTIONS[styleConfig.type], //Дефолтный стиль
    ...evaluateExpression(styleConfig.options, layerItemProperties), //Вычисление стиля слоя
    ...layerItemStyle //Хардкодный внешний стиль
  }

  //Замена названий и идентификаторов изображений на ссылки перед отрисовкой
  if (PARSE_ICON_LINKS[styleConfig.type] && layerIconDictionary) {
    PARSE_ICON_LINKS[styleConfig.type].forEach((iconPath) => {
      options = parseIconFields(iconPath, options, layerIconDictionary)
    })
  }

  return options
}

function parseIconFields(iconFieldPath, options, layerIconDictionary) {
  const iconFieldPathArray = iconFieldPath.split('.')
  const fieldName = iconFieldPathArray[0]

  if (!options[fieldName]) {
    return options
  }

  if (typeof options[fieldName] === 'object') {
    return {
      ...options,
      [fieldName]: parseIconFields(iconFieldPathArray.slice(1).join('.'), options[fieldName], layerIconDictionary)
    }
  }

  return {
    ...options,
    url: options.url || layerIconDictionary[options[fieldName]] //Не перезатирать url указанный явно
  }
}

export function getLegendIconDataUrl(styleConfig, layerIconDictionary) {
  const canvas = document.createElement('canvas')
  canvas.width = CANVAS_WIDTH
  canvas.height = CANVAS_HEIGHT
  const ctx = canvas.getContext('2d')

  let styleConfigArray = null
  if (Array.isArray(styleConfig)) {
    //Рисовать иконку будем по массиву настроек стилей
    styleConfigArray = styleConfig
  } else if (typeof styleConfig === 'object') {
    //Одиночный объект преобразуем в массив
    styleConfigArray = [styleConfig]
  } else {
    //Если стиль - не массив и не объект, то это ошибка
    return null
  }

  let drawCount = 0
  for (let styleConfigItem of styleConfigArray) {
    //Для CanvasMarker просто вернем ссылку на изображение
    //В текущей версии не комбинируем картинку в легенде с другими объектами
    if (styleConfigItem.type === 'CanvasMarker' && styleConfigItem.options && styleConfigItem.options.img) {
      const evaluatedStyleItem = evaluateStyleOptions(
        styleConfigItem,
        {
          //Это небольшой костыль, передаем флаг legend: true,
          // а в настройках стиля можем сделать выражения с ним,
          // чтобы хардкодить условное обозначение в левой панели
          legend: true
        },
        {},
        layerIconDictionary
      )

      return evaluatedStyleItem.img.url
    }

    const drawFn = DRAW_LEGEN_ICON_BY_STYLE[styleConfigItem.type]
    if (drawFn) {
      drawFn(
        ctx,
        evaluateStyleOptions(
          styleConfigItem,
          {
            //Это небольшой костыль, передаем флаг legend: true,
            // а в настройках стиля можем сделать выражения с ним,
            // чтобы хардкодить условное обозначение в левой панели
            legend: true
          },
          {},
          layerIconDictionary
        )
      )
      //Подсчитаем сколько объектов нарисовали в канвас
      drawCount++
    }
  }

  //Если ничего не удалось нарисовать в канвас, то нет иконки
  if (!drawCount) {
    return null
  }

  return canvas.toDataURL('image/png')
}

function drawCircleMarkerLegendIcon(ctx, { color = '#fff', opacity = 1, weight = 3, fillColor, fillOpacity = 1, radius }) {
  const width = ctx.canvas.width
  let iconRadius = width / 2 - weight
  if (radius && radius < iconRadius) {
    iconRadius = radius
  }

  ctx.strokeStyle = color
  ctx.lineWidth = weight
  ctx.fillStyle = fillColor

  ctx.beginPath()
  ctx.arc(width / 2, width / 2, iconRadius, 0, 2 * Math.PI)
  ctx.globalAlpha = opacity
  ctx.stroke()

  ctx.globalAlpha = fillOpacity
  ctx.fill()
}

function drawPolylineLegendIcon(ctx, { color = '#000', opacity = 1, dashArray, weight }) {
  const width = ctx.canvas.width

  ctx.strokeStyle = color
  ctx.lineWidth = weight

  ctx.beginPath()
  ctx.moveTo(0, width)
  if (!!dashArray) {
    //dashArray - строка с числами через запятую, преобразуем ее в массив чисел как нужно для канваса
    ctx.setLineDash(dashArray.split(/\s*,\s*/).map((value) => +value))
  }
  ctx.lineTo(width, 0)
  ctx.globalAlpha = opacity
  ctx.stroke()
}

function drawPolylineDecoratedLegendIcon(ctx, options) {
  drawPolylineLegendIcon(ctx, options)
}

function drawGeoJsonLegendIcon(ctx, styleOptions) {
  //GeoJSON применяется в линейных инфослоях, рисуем егов легенде линией
  return drawPolylineLegendIcon(ctx, styleOptions.style) //Настройки geojson вложены в поле style
}

function HEXtoRGB(hex) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  hex = hex.replace(shorthandRegex, function(m, r, g, b) {
    return r + r + g + g + b + b
  })
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null
}

function drawVectorTileIcon(ctx, options) {
  const drawFn = DRAW_LEGEN_ICON_BY_STYLE[options.default.type]
  if (drawFn) {
    drawFn(ctx, options.default.options)
  }
}



// WEBPACK FOOTER //
// ./src/helpers/leaflet-layer-style-utils.js